// src/components/SizeGuide.tsx
// Reusable size-guide content — rendered both on the standalone /size-guide page
// and inside a modal on the product page. Content lives in src/data/sizeGuide.ts.
//
// `fit` picks the chart: most of the catalog is the unisex blank, but the crop
// top is a women's cut (AS Colour 4062) whose sizing advice is the OPPOSITE of
// the unisex "size down for a fitted look". The product page passes
// fitForProductName(product.name); the standalone page shows every chart.

import React from "react";
import {
  UNISEX_TEE_SIZES,
  WOMENS_CROP_SIZES,
  WOMENS_FIT_GUIDANCE,
  CROP_FIT_GUIDANCE,
  HOW_TO_MEASURE,
  SIZE_GUIDE_FOOTNOTE,
  Fit,
} from "../data/sizeGuide";

interface SizeGuideProps {
  /** Which chart to show. "all" shows every fit (the standalone page). */
  fit?: Fit | "all";
}

const UnisexChart: React.FC = () => (
  <>
    <div className="overflow-x-auto">
      <table className="size-guide-table">
        <caption>Unisex T-Shirts — measurements in inches</caption>
        <thead>
          <tr>
            <th>Size</th>
            <th>Chest (to fit)</th>
            <th>Length</th>
            <th>
              Women's<span className="font-normal"> (relaxed)</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {UNISEX_TEE_SIZES.map((row) => (
            <tr key={row.size}>
              <td className="font-semibold text-brown-dark">{row.size}</td>
              <td>{row.chestToFitIn}"</td>
              <td>{row.lengthIn}"</td>
              <td>{row.womensRelaxed}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Women's fit guidance — the part that de-confuses unisex sizing */}
    <div className="rounded-md border border-accent bg-accent-light p-4 flex flex-col gap-2">
      <h3 className="text-lg playfair-text text-brown-dark">
        {WOMENS_FIT_GUIDANCE.heading}
      </h3>
      <ul className="list-disc pl-5 flex flex-col gap-1 text-sm text-gray-700 lora-text">
        {WOMENS_FIT_GUIDANCE.points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </div>
  </>
);

const CropChart: React.FC = () => (
  <>
    <div className="overflow-x-auto">
      <table className="size-guide-table">
        <caption>Women's Crop Top — measurements in inches</caption>
        <thead>
          <tr>
            <th>Size</th>
            <th>Chest (to fit)</th>
            <th>
              Length<span className="font-normal"> (cropped)</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {WOMENS_CROP_SIZES.map((row) => (
            <tr key={row.size}>
              <td className="font-semibold text-brown-dark">{row.size}</td>
              <td>{row.chestToFitIn}"</td>
              <td>{row.lengthIn}"</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="rounded-md border border-accent bg-accent-light p-4 flex flex-col gap-2">
      <h3 className="text-lg playfair-text text-brown-dark">
        {CROP_FIT_GUIDANCE.heading}
      </h3>
      <ul className="list-disc pl-5 flex flex-col gap-1 text-sm text-gray-700 lora-text">
        {CROP_FIT_GUIDANCE.points.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </div>
  </>
);

const SizeGuide: React.FC<SizeGuideProps> = ({ fit = "unisex" }) => {
  const showUnisex = fit === "unisex" || fit === "all";
  const showCrop = fit === "womensCrop" || fit === "all";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl playfair-text text-brown-dark">Size Guide</h2>
        <p className="text-sm text-gray-600 lora-text">
          {showUnisex && showCrop ? (
            <>
              Most of our apparel is a <strong>unisex fit</strong>; the crop top
              is a <strong>women's cut</strong>. Use your body measurements
              below to find the closest match.
            </>
          ) : showCrop ? (
            <>
              This piece is a <strong>women's cropped fit</strong>. Use your
              body measurements below to find the closest match.
            </>
          ) : (
            <>
              Our apparel is a <strong>unisex fit</strong>. Use your body
              measurements below to find the closest match.
            </>
          )}
        </p>
      </div>

      {showUnisex && <UnisexChart />}
      {showCrop && <CropChart />}

      {/* How to measure */}
      <div className="flex flex-col gap-2">
        <h3 className="text-lg playfair-text text-brown-dark">
          How to measure
        </h3>
        <dl className="flex flex-col gap-2 text-sm text-gray-700 lora-text">
          {HOW_TO_MEASURE.map((m) => (
            <div key={m.label} className="flex flex-col sm:flex-row sm:gap-2">
              <dt className="font-semibold text-brown-dark shrink-0 sm:w-28">
                {m.label}
              </dt>
              <dd>{m.text}</dd>
            </div>
          ))}
        </dl>
      </div>

      <p className="text-xs text-gray-500 italic">{SIZE_GUIDE_FOOTNOTE}</p>
    </div>
  );
};

export default SizeGuide;
